import { FormEditorTypes } from "@/components/forms/form-editor.config.ts";
import { useFormEditor } from "@/components/forms/form-editor-context.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApiFormElementEdit } from "@/types/forms/form-edit.types.ts";
import { ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import { ApiFormElementType } from "@/types/forms/form.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { DraggableProvided } from "@hello-pangea/dnd";
import {
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  FontAwesomeIcon,
  Stack,
  Title,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export const useFormElementUtils = () => {
  const { t } = useTranslation();
  const { formValues, formEditorType, readOnly, openEditElement, handleDuplicateElement } =
    useFormEditor();
  const { handleAddElement, handleAddScale, handleAddGoalScale, handleAddSection, handleAddPage } =
    useFormEditor();
  const { isFeatureEnabled } = usePermissions();
  const isGoalsEnabled = isFeatureEnabled(ApiFeatureType.goals);
  const isCustomFieldsEnabled = isFeatureEnabled(ApiFeatureType.custom_fields);
  const isPerformanceReview = [
    FormEditorTypes.performanceReview,
    FormEditorTypes.performanceReviewTemplate,
  ].includes(formEditorType);
  const isSurvey = [FormEditorTypes.survey, FormEditorTypes.surveyTemplate].includes(
    formEditorType
  );

  const renderEditBlock = (
    pageIndex: number,
    sectionIndex?: number,
    elementIndex?: number,
    dragHandleProps?: DraggableProvided["dragHandleProps"]
  ) => {
    const onePage = formValues.pages.length == 1;
    const oneSection = formValues.pages[pageIndex].sections.length == 1;

    let title = (pageIndex + 1).toString();
    if (sectionIndex !== undefined) {
      title = `${onePage ? "" : pageIndex + 1 + "."}${sectionIndex + 1}`;
    }
    if (sectionIndex !== undefined && elementIndex !== undefined) {
      title = `${onePage ? "" : pageIndex + 1 + "."}${oneSection ? "" : sectionIndex + 1 + "."}${elementIndex + 1}`;
    }

    if (readOnly) {
      return (
        <Stack items={"end"} className={"w-28 min-h-7 shrink-0"} {...dragHandleProps}>
          <Title size={"xs"} header={title} />
        </Stack>
      );
    }

    return (
      <Stack items={"end"} className={"w-28 min-h-7 shrink-0"}>
        <Title size={"xs"} header={title} className={"group-hover:hidden"} />
        <Stack direction={"horizontal"} className={"hidden group-hover:flex"}>
          <DropdownMenu
            trigger={
              <ButtonIcon variant={"plain"} icon={<FontAwesomeIcon icon={"fa-light fa-plus"} />} />
            }
            disableAutoUpdate={true}
          >
            <DropdownMenuItem
              title={t("surveys:question_type_text")}
              description={t("surveys:question_type_text_caption")}
              onClick={() => {
                handleAddElement(ApiFormElementType.text, {
                  pageIndex,
                  sectionIndex,
                  elementIndex,
                });
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-text-size" />}
            />
            <DropdownMenuItem
              title={t("surveys:question_type_open_answer")}
              description={t("surveys:question_type_open_answer_caption")}
              onClick={() => {
                handleAddElement(ApiFormElementType.open_answer, {
                  pageIndex,
                  sectionIndex,
                  elementIndex,
                });
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-font-case" />}
            />
            <DropdownMenuItem
              title={t("surveys:question_type_select")}
              description={t("surveys:question_type_select_caption")}
              onClick={() => {
                handleAddElement(ApiFormElementType.select, {
                  pageIndex,
                  sectionIndex,
                  elementIndex,
                });
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-list-check" />}
            />
            <DropdownMenuItem
              title={t("surveys:question_type_scale")}
              description={t("surveys:question_type_scale_caption")}
              onClick={() => {
                handleAddScale({
                  pageIndex,
                  sectionIndex,
                  elementIndex,
                });
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-chart-pyramid" />}
            />
            <DropdownMenuItem
              title={t("surveys:question_type_employee_nps")}
              description={t("surveys:question_type_employee_nps_caption")}
              onClick={() => {
                handleAddElement(ApiFormElementType.employee_nps, {
                  pageIndex,
                  sectionIndex,
                  elementIndex,
                });
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-comment-smile" />}
            />
            {isSurvey && (
              <DropdownMenuItem
                title={t("surveys:question_type_file")}
                description={t("surveys:question_type_file_caption")}
                onClick={() => {
                  handleAddElement(ApiFormElementType.file, {
                    pageIndex,
                    sectionIndex,
                    elementIndex,
                  });
                }}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-paperclip" />}
              />
            )}
            {isGoalsEnabled && isPerformanceReview && (
              <DropdownMenuItem
                title={t("performance_review:question_type_goal")}
                description={t("performance_review:question_type_goal_caption")}
                onClick={() => {
                  handleAddGoalScale({
                    pageIndex,
                    sectionIndex,
                    elementIndex,
                  });
                }}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-bullseye-arrow" />}
              />
            )}
            {isCustomFieldsEnabled && isSurvey ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  title={t("surveys:write_to_field_title")}
                  description={t("surveys:write_to_field_caption")}
                  onClick={() => {
                    handleAddElement(ApiFormElementType.write_to_field, {
                      pageIndex,
                      sectionIndex,
                      elementIndex,
                    });
                  }}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-input-text" />}
                />
              </>
            ) : (
              <></>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              title={t("common:page")}
              description={t("surveys:question_type_page_caption")}
              onClick={() => {
                handleAddPage();
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-file" />}
            />
            <DropdownMenuItem
              title={t("common:section")}
              description={t("surveys:question_type_section_caption")}
              onClick={() => {
                handleAddSection(pageIndex);
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-h1" />}
            />
          </DropdownMenu>
          <ButtonIcon
            variant={"plain"}
            icon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              openEditElement(pageIndex, sectionIndex, elementIndex);
            }}
          />
          {sectionIndex !== undefined && elementIndex !== undefined && (
            <ButtonIcon
              variant={"plain"}
              icon={<FontAwesomeIcon icon={"fa-light fa-clone"} />}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleDuplicateElement(pageIndex, sectionIndex, elementIndex);
              }}
            />
          )}
          <div
            {...dragHandleProps}
            className={twMerge(
              "justify-center text-brand-dark hover:bg-dark/5",
              "rounded-xs outline-0 gap-2 items-center flex transition-all ease-in overflow-hidden cursor-pointer",
              "justify-center text-brand-dark hover:bg-dark/5",
              "w-7 h-7 shrink-0 "
            )}
          >
            <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
          </div>
        </Stack>
      </Stack>
    );
  };

  const getQuestionTitleWithSubtitle = (
    element: ApiFormElementEdit,
    viewElement: ApiFormViewElement
  ) => {
    let title = "";
    let subtitle: string | null = null;
    let isRequired = false;
    switch (element.type) {
      case ApiFormElementType.text:
        title = t("surveys:question_type_text_full");
        subtitle = element.text_settings?.title || null;
        break;
      case ApiFormElementType.open_answer:
        title = t("surveys:question_type_open_answer");
        subtitle = element.open_answer_settings?.title || null;
        isRequired = element.open_answer_settings?.is_required || false;
        break;
      case ApiFormElementType.file:
        title = t("surveys:question_type_file");
        subtitle = element.file_settings?.title || null;
        isRequired = element.file_settings?.is_required || false;
        break;
      case ApiFormElementType.scale:
        title = `${t("surveys:question_type_scale")}  ·  ${viewElement.scale_settings?.form_scale?.title}`;
        subtitle = element.scale_settings?.title || null;
        isRequired = element.scale_settings?.is_required || false;
        break;
      case ApiFormElementType.goals:
        title = `${t("performance_review:question_type_goal")}  ·  ${viewElement.goals_settings?.form_scale?.title}`;
        subtitle = element.goals_settings?.title || null;
        isRequired = element.goals_settings?.is_required || false;
        break;
      case ApiFormElementType.select:
        title = t("surveys:question_type_select");
        subtitle = element.select_settings?.title || null;
        isRequired = element.select_settings?.is_required || false;
        break;
      case ApiFormElementType.employee_nps:
        title = t("surveys:question_type_employee_nps");
        subtitle =
          element.employee_nps_settings?.title || t("surveys:employee_nps_element_title_default");
        isRequired = element.employee_nps_settings?.is_required || false;
        break;
      case ApiFormElementType.write_to_field:
        title = t("surveys:write_to_field_title");
        subtitle = element.write_to_field_settings?.title || null;
        isRequired = element.write_to_field_settings?.is_required || false;
        break;
    }
    return (
      <>
        <Title size={"xs"} header={title} />
        <Title
          header={subtitle ?? t("surveys:question_title")}
          className={subtitle ? "" : "text-secondary"}
          size={"md"}
          required={isRequired}
        />
      </>
    );
  };

  return {
    renderEditBlock,
    getQuestionTitleWithSubtitle,
  };
};
